'use client';

import { useEffect, useRef, forwardRef, useCallback } from 'react';
import intlTelInput, { Iti } from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import styles from './phone-input.module.scss';

interface PhoneInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  error?: string;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      placeholder = 'Enter your phone number',
      value = '',
      onChange,
      className = '',
      error,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const itiRef = useRef<Iti | null>(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      if (!inputRef.current || itiRef.current) return;

      // Initialize intl-tel-input
      itiRef.current = intlTelInput(inputRef.current, {
        initialCountry: 'us',
        preferredCountries: ['us', 'gb', 'ca', 'au'],
        separateDialCode: true,
        formatOnDisplay: true,
        autoPlaceholder: 'aggressive',
        customPlaceholder: (selectedCountryPlaceholder: string) => {
          return placeholder || selectedCountryPlaceholder;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      const handleInput = () => {
        if (itiRef.current && onChangeRef.current) {
          let phoneNumber = itiRef.current.getNumber();
          const inputValue = inputRef.current?.value || '';

          if (!phoneNumber && inputValue) {
            const selectedCountryData = itiRef.current.getSelectedCountryData();
            const dialCode = selectedCountryData.dialCode;

            if (inputValue && !inputValue.startsWith('+')) {
              phoneNumber = `+${dialCode}${inputValue}`;
            } else {
              phoneNumber = inputValue;
            }
          }

          if (phoneNumber) {
            onChangeRef.current(phoneNumber);
          }
        }
      };

      const handleCountryChange = () => {
        if (itiRef.current && onChangeRef.current) {
          const inputValue = inputRef.current?.value || '';

          if (inputValue) {
            const selectedCountryData = itiRef.current.getSelectedCountryData();
            const dialCode = selectedCountryData.dialCode;
            const phoneNumber = `+${dialCode}${inputValue}`;
            onChangeRef.current(phoneNumber);
          }
        }
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
          'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Enter',
        ];
        if (allowedKeys.includes(e.key)) return;
        if (e.ctrlKey || e.metaKey) return;
        if (/^\d$/.test(e.key)) return;
        if (e.key === '+' && (inputRef.current?.selectionStart ?? 0) === 0) return;
        e.preventDefault();
      };

      const el = inputRef.current;
      // Use intl-tel-input events instead of native input events
      el.addEventListener('keydown', handleKeyDown);
      el.addEventListener('input', handleInput);
      el.addEventListener('blur', handleInput);
      el.addEventListener('keyup', handleInput);
      el.addEventListener('countrychange', handleCountryChange);

      // Cleanup function
      return () => {
        el.removeEventListener('keydown', handleKeyDown);
        el.removeEventListener('input', handleInput);
        el.removeEventListener('blur', handleInput);
        el.removeEventListener('keyup', handleInput);
        el.removeEventListener('countrychange', handleCountryChange);
        try {
          itiRef.current?.destroy();
        } catch {}
        itiRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // синхронизация внешнего value → виджета
    useEffect(() => {
      if (!itiRef.current) return;
      if (typeof value === 'string' && value !== itiRef.current.getNumber()) {
        try {
          itiRef.current.setNumber(value);
        } catch {}
      }
    }, [value]);

    // Стабильный ref callback, чтобы не терять фокус
    const handleRef = useCallback(
      (node: HTMLInputElement | null) => {
        // пробрасываем реф наружу, если нужен
        if (typeof ref === 'function') ref(node as HTMLInputElement);
        else if (ref)
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node;
        inputRef.current = node;
      },
      [ref]
    );

    return (
      <div className={[styles.root, className].join(' ')}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles['input-container']}>
          <input
            ref={handleRef}
            type="tel"
            className={`${styles.input} ${error ? styles.error : ''}`}
            placeholder={placeholder}
          />
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
